import React, { useEffect, useMemo, useRef, useState } from "react";

function safeName(t) {
    return (t?.name || "Unknown").toString();
}

export default function AudioNFTPlayerComponent({
                                                    viewAddress,
                                                    isLoading,
                                                    isRefreshing,
                                                    tokens, // expects: audio-capable tokens already (mediaType=audio, has audioUrl)
                                                }) {
    // Player state lives HERE now (Option A)
    const audioRef = useRef(null);
    const [audioSearch, setAudioSearch] = useState("");
    const [audioActiveIndex, setAudioActiveIndex] = useState(0);
    const [audioShuffle, setAudioShuffle] = useState(false);
    const [audioRepeatOne, setAudioRepeatOne] = useState(false);

    const filteredList = useMemo(() => {
        const q = audioSearch.trim().toLowerCase();
        const list = (tokens || [])
            .filter((t) => !!t?.audioUrl)
            .filter((t) => (q ? safeName(t).toLowerCase().includes(q) : true))
            .slice()
            .sort((a, b) => safeName(a).localeCompare(safeName(b)));

        return list;
    }, [tokens, audioSearch]);

    const audioCurrent = filteredList[audioActiveIndex] || null;

    // Keep activeIndex valid when list changes
    useEffect(() => {
        if (!filteredList.length) {
            setAudioActiveIndex(0);
            return;
        }
        if (audioActiveIndex > filteredList.length - 1) setAudioActiveIndex(0);
    }, [filteredList, audioActiveIndex]);

    // Autoplay on track change (best-effort)
    useEffect(() => {
        const el = audioRef.current;
        if (!el) return;
        if (!audioCurrent?.audioUrl) return;
        el.load();
        el.play().catch(() => {});
    }, [audioCurrent?.audioUrl]);

    const playIndex = (idx) => {
        if (!filteredList.length) return;
        const next = Math.max(0, Math.min(idx, filteredList.length - 1));
        setAudioActiveIndex(next);
    };

    const nextTrack = () => {
        if (!filteredList.length) return;
        if (audioShuffle) {
            const r = Math.floor(Math.random() * filteredList.length);
            setAudioActiveIndex(r);
            return;
        }
        setAudioActiveIndex((i) => (i + 1) % filteredList.length);
    };

    const prevTrack = () => {
        if (!filteredList.length) return;
        setAudioActiveIndex((i) => (i - 1 + filteredList.length) % filteredList.length);
    };

    const togglePlay = async () => {
        const el = audioRef.current;
        if (!el) return;
        if (el.paused) await el.play();
        else el.pause();
    };

    const onEnded = () => {
        if (audioRepeatOne) {
            const el = audioRef.current;
            if (el) {
                el.currentTime = 0;
                el.play().catch(() => {});
            }
            return;
        }
        nextTrack();
    };

    return (
        <div>
            <p className="audio-player-subtitle">
                Viewing wallet: <strong className="mono">{viewAddress}</strong>
            </p>

            {(isLoading || isRefreshing) && (
                <div className="loading-indicator">
          <span>
            Loading Wallet Contents. This may take a few minutes to complete. This message will disappear when finished.
          </span>
                    <div className="loading-spinner" />
                </div>
            )}

            <div className="audio-player-layout">
                <main className="audio-player-nowplaying">
                    {audioCurrent ? (
                        <>
                            <div className="audio-nowplaying-card">
                                <div className="audio-nowplaying-media">
                                    {audioCurrent.image && audioCurrent.image !== "No image URL" ? (
                                        <img
                                            src={audioCurrent.image}
                                            alt={safeName(audioCurrent)}
                                            className="audio-nowplaying-image"
                                        />
                                    ) : (
                                        <div className="audio-nowplaying-image-placeholder">No image</div>
                                    )}
                                </div>

                                <div className="audio-nowplaying-info">
                                    <h2 className="audio-nowplaying-title">{safeName(audioCurrent)}</h2>
                                    <p className="audio-nowplaying-title">{audioCurrent.description}</p>
                                    <br />
                                    <p className="audio-nowplaying-title">{audioCurrent.external_url}</p>
                                    <br />

                                    <div className="audio-nowplaying-links">
                                        <a href={`https://solscan.io/token/${audioCurrent.mint}`} target="_blank" rel="noreferrer">
                                            View Mint on Solscan
                                        </a>

                                        {audioCurrent.collectionKey && (
                                            <a
                                                href={`https://solscan.io/token/${audioCurrent.collectionKey}`}
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                View Collection on Solscan
                                            </a>
                                        )}

                                        {audioCurrent.uri && audioCurrent.uri !== "No Metadata URI" && (
                                            <a href={audioCurrent.uri} target="_blank" rel="noreferrer">
                                                Open Metadata JSON
                                            </a>
                                        )}
                                    </div>

                                    <audio
                                        ref={audioRef}
                                        className="audio-player-element"
                                        controls
                                        onEnded={onEnded}
                                        src={audioCurrent.audioUrl}
                                    />
                                </div>
                            </div>

                            <div className="audio-player-controls">
                                <button onClick={prevTrack} type="button">⏮ Prev</button>
                                <button onClick={togglePlay} type="button">⏯ Play/Pause</button>
                                <button onClick={nextTrack} type="button">Next ⏭</button>

                                <button
                                    type="button"
                                    className={audioShuffle ? "active-toggle" : ""}
                                    onClick={() => setAudioShuffle((v) => !v)}
                                    title="Shuffle"
                                >
                                    🔀 Shuffle
                                </button>

                                <button
                                    type="button"
                                    className={audioRepeatOne ? "active-toggle" : ""}
                                    onClick={() => setAudioRepeatOne((v) => !v)}
                                    title="Repeat current track"
                                >
                                    🔁 Repeat 1
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className="audio-nowplaying-card">
                            <p>Select a track from the playlist.</p>
                        </div>
                    )}
                </main>

                <aside className="audio-player-playlist">
                    <div className="audio-player-search">
                        <label>Search by name</label>
                        <input
                            type="text"
                            value={audioSearch}
                            onChange={(e) => setAudioSearch(e.target.value)}
                            placeholder="Start typing…"
                        />
                    </div>

                    <div className="audio-player-playlist-meta">
                        <p>
                            Tracks found: <strong>{filteredList.length}</strong>
                        </p>
                    </div>

                    <div className="audio-player-tracklist">
                        {filteredList.map((t, idx) => {
                            const isActive = idx === audioActiveIndex;
                            return (
                                <button
                                    key={t.mint || `${t.name}-${idx}`}
                                    className={`audio-track-row ${isActive ? "active" : ""}`}
                                    onClick={() => playIndex(idx)}
                                    type="button"
                                >
                                    <div className="audio-track-row-title">{safeName(t)}</div>
                                    <div className="audio-track-row-sub mono">
                                        {t.type} {t.collectionKey ? `• ${String(t.collectionKey).slice(0, 8)}…` : ""}
                                    </div>
                                </button>
                            );
                        })}

                        {!filteredList.length && !isLoading && !isRefreshing && (
                            <p style={{ opacity: 0.85 }}>
                                No audio NFTs found for this wallet (or no audioUrl present in metadata).
                            </p>
                        )}
                    </div>
                </aside>
            </div>
        </div>
    );
}
